import { useEffect, useRef } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { TrackManager } from '../services/TrackManager';
import { PilotData } from '../types';

interface UseTrackManagerOptions {
  map: MapLibreMap | null;
  pilots: Map<string, PilotData>;
  raceId?: string;
}

export const useTrackManager = ({ map, pilots, raceId }: UseTrackManagerOptions) => {
  const trackManagerRef = useRef<TrackManager>(new TrackManager());

  // Set map when it changes
  useEffect(() => {
    if (map) {
      trackManagerRef.current.setMap(map);
    }
  }, [map]);

  // Update tracks when pilots change
  useEffect(() => {
    if (!map) return;

    // Ensure the map has been set in the track manager before updating pilots
    if (trackManagerRef.current) {
      trackManagerRef.current.setMap(map);
    }

    pilots.forEach((pilot) => {
      trackManagerRef.current.addOrUpdatePilot(pilot);
    });

    // Remove tracks for pilots that no longer exist
    const currentPilotIds = new Set(Array.from(pilots.keys()));
    const allTracks = trackManagerRef.current.getAllTracks();

    allTracks.forEach((track, pilotId) => {
      if (!currentPilotIds.has(pilotId)) {
        trackManagerRef.current.removePilot(pilotId);
      }
    });
  }, [map, pilots]);

  // Style change handling is now done internally by TrackManager
  // This prevents duplicate event listeners and ensures persistence

  return {
    trackManager: trackManagerRef.current,
    loadHistoricalTrack: (pilotId: string) => {
      console.log('🎯 loadHistoricalTrack hook called for pilot:', pilotId, 'race:', raceId);
      if (raceId) {
        trackManagerRef.current.loadHistoricalTrack({
          pilotId,
          raceId
        });
      } else {
        console.warn('❌ No raceId available for loading historical track');
      }
    },
    toggleHistoricalTrack: (pilotId: string, show: boolean) => {
      console.log('🎯 toggleHistoricalTrack hook called for pilot:', pilotId, 'show:', show);
      trackManagerRef.current.toggleHistoricalTrack(pilotId, show);
    },
    clearTrack: (pilotId: string) => {
      trackManagerRef.current.clearTrack(pilotId);
    },
    clearAllTracks: () => {
      trackManagerRef.current.clearAllTracks();
    },
    setShowHistorical: (show: boolean) => {
      trackManagerRef.current.setRenderOptions({
        showHistorical: show
      });
    }
  };
};